import { getFirstName } from '@bybeam/formatting';
import { FeatureName } from '@bybeam/platform-types';
import Button from '@platform-ui-common/components/Button/index.js';
import Popper from '@platform-ui-common/components/Popper/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import usePartnerPrograms from '@platform-ui-common/hooks/contexts/PartnerPrograms';
import usePartner from '@platform-ui-common/hooks/contexts/usePartner.js';
import ApplicantRoutes from '@platform-ui/application/utils/ApplicantRoutes.js';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import ClaimRoutes from '@platform-ui/claim/ClaimRoutes.js';
import { checkFeaturesAnyProgram } from '@platform-ui/utilities/checkFeature.js';

export interface MyProfilePopperProps {
  onClickClose: () => void;
  isOpen: boolean;
  anchorEl?: HTMLButtonElement;
}

export default function MyProfilePopper({
  onClickClose,
  isOpen,
  anchorEl,
}: MyProfilePopperProps): JSX.Element {
  const { user } = useAuth();
  const partner = usePartner();
  const { programs } = usePartnerPrograms();

  if (!user?.gcip || !user?.core) return null;

  return (
    <Popper isOpen={isOpen} onClickClose={onClickClose} anchorEl={anchorEl} title="My Profile">
      <div className="text-left py-14 lg:py-4">
        {user.core.isAnonymous ? (
          <Typography variant="body">You&apos;re signed in as a guest.</Typography>
        ) : (
          <Typography variant="body">
            Hi, <strong>{getFirstName(user.core.name)}</strong>!
          </Typography>
        )}
      </div>
      <div className="flex flex-col text-left items-start gap-8 lg:gap-5">
        <Button
          variant="text"
          link={ApplicantRoutes.APPLICATION_PORTAL}
          // For some reason the popper does not automatically close when this button is
          // clicked within the context of an in-progress application
          onClick={onClickClose}
        >
          My Applications
        </Button>
        {!user.core.isAnonymous &&
          checkFeaturesAnyProgram(programs, [
            FeatureName.PaymentsPartnerIssued,
            FeatureName.PaymentsApplicants,
            FeatureName.PaymentsDirectDeposit,
          ]) && (
            <Button variant="text" link={ClaimRoutes.BANK_ACCOUNT}>
              Bank Account Details
            </Button>
          )}
        <Button variant="text" link={ApplicantRoutes.PROFILE}>
          {user.core.isAnonymous ? 'Link your account' : 'Profile'}
        </Button>
        <Button variant="text" link={'https://www.bybeam.co/privacy-policy'} external>
          Privacy Policy
        </Button>
        <Button variant="text" link={'https://www.bybeam.co/terms-of-service'} external>
          Terms & Conditions
        </Button>
        <Button variant="text" link={`mailto:${partner.email}`} external>
          Contact Us
        </Button>
      </div>
    </Popper>
  );
}
